import { CheckOutlined, Close, EditOutlined } from "@mui/icons-material";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocalization } from "../hooks/useLocalization";
import { TextFieldWithCounter } from "./TextFieldWithCounter";

export interface EditableTitleProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  onCancel?: () => void;
  isEditable?: boolean;
  maxLength?: number;
  fontSize?: number;
  fontWeight?: number;
  placeholder?: string;
  textFieldMaxWidth?: number;
  fullWidth?: boolean;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
}

export const EditableTitle: React.FC<EditableTitleProps> = ({
  value,
  onSave,
  onCancel,
  isEditable = true,
  maxLength = 28,
  fontWeight = 700,
  placeholder,
  textFieldMaxWidth = 500,
  fullWidth = true,
  error = false,
  errorMessage,
  disabled,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState<string>(value);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const isSaveDisabled =
    !currentValue.trim() || currentValue.length > maxLength || disabled;

  const handleSave = (
    event?: React.FormEvent | React.KeyboardEvent | React.MouseEvent,
  ) => {
    event?.preventDefault();

    if (isSaveDisabled) {
      return;
    }

    const handleSaveAsync = async () => {
      try {
        await onSave(currentValue);
        setIsEditing(false);
      } catch {
        // Keep editing mode open if save fails
      }
    };

    void handleSaveAsync();
  };

  const handleCancel = () => {
    setCurrentValue(value);
    setIsEditing(false);
    onCancel?.();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(event.target.value);
  };

  return (
    <Box
      sx={{
        height: 48,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      {isEditing ? (
        <>
          <form
            onSubmit={handleSave}
            style={{ maxWidth: textFieldMaxWidth, width: "100%" }}
          >
            <TextFieldWithCounter
              error={error}
              fullWidth={fullWidth}
              helperText={errorMessage}
              isDisabled={disabled}
              maxLength={maxLength}
              multiline={false}
              onChange={handleChange}
              placeholder={
                placeholder ?? t("components.editableTitle.placeholder")
              }
              value={currentValue}
            />
          </form>
          <Button
            disabled={isSaveDisabled}
            onClick={handleSave}
            sx={{
              minWidth: "auto",
              padding: "10px",
              transition: "none",
            }}
            variant="contained"
          >
            <CheckOutlined sx={{ fontSize: 20 }} />
          </Button>
          <Button
            onClick={handleCancel}
            sx={{
              minWidth: "auto",
              padding: "10px",
              color: theme.colors.secondary[5],
              "&:hover": {
                backgroundColor: theme.colors.secondary[1],
              },
            }}
            variant="text"
          >
            <Close sx={{ fontSize: 20 }} />
          </Button>
        </>
      ) : (
        <>
          <Typography
            sx={{
              fontWeight,
              fontSize: { xs: 18, md: 24 },
              color: theme.colors.text[5],
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {currentValue}
          </Typography>
          {isEditable && (
            <Button
              onClick={handleEdit}
              sx={{
                minWidth: "auto",
                padding: "10px",
                transition: "none",
                color: theme.colors.secondary[5],
                "&:hover": {
                  backgroundColor: theme.colors.secondary[1],
                },
              }}
              variant="text"
            >
              <EditOutlined sx={{ fontSize: 20 }} />
            </Button>
          )}
        </>
      )}
    </Box>
  );
};
